import { LitElement, html, nothing } from 'lit';
import {
  hasStoredConsentResponse,
  persistAcceptedConsent,
  persistDeclinedConsent,
  suppressDefaultConsentUi,
  openCookiePreferences,
} from '../utilities/cookies';

export class CookieBanner extends LitElement {
  static REGION_REDIRECT_DEFER_TIMEOUT_MS = 10000;

  static get properties() {
    return {
      message: { type: String },
      preferences_text: { type: String },
      accept_text: { type: String },
      decline_text: { type: String },
      title: { type: String },
      icon_url: { type: String },
      visible: { type: Boolean, state: true },
      consentPending: { type: Boolean, state: true },
      isOpen: { type: Boolean, state: true },
    };
  }

  constructor() {
    super();
    this.icon_url = '';
    this.visible = false;
    this.consentPending = false;
    this.isOpen = false;
    this.awaitingRegionRedirectResolution = false;
    this.regionRedirectDeferTimeout = null;
    this.boundHandleRegionRedirectStateChange =
      this.handleRegionRedirectStateChange.bind(this);
  }

  shouldDeferForRegionRedirect() {
    if (localStorage.getItem('locced')) {
      return false;
    }

    if (window.theme?.inThemeEditor || window.suppressRedirect) {
      return false;
    }

    return true;
  }

  isRedirectDialogOpen() {
    return !!document.getElementById('redirect');
  }

  isGeolocationResolved() {
    return !!window.eventKeeper?.heard('geolocator:announce');
  }

  isRegionRedirectBlocking() {
    if (!this.shouldDeferForRegionRedirect()) {
      return false;
    }

    if (this.isRedirectDialogOpen()) {
      return true;
    }

    return (
      this.awaitingRegionRedirectResolution && !this.isGeolocationResolved()
    );
  }

  suspend() {
    this.isOpen = false;
    this.visible = false;
    this.syncHostState();
    this.dispatchStateChange();
  }

  clearRegionRedirectDeferTimeout() {
    if (this.regionRedirectDeferTimeout) {
      clearTimeout(this.regionRedirectDeferTimeout);
      this.regionRedirectDeferTimeout = null;
    }
  }

  handleRegionRedirectStateChange(event) {
    if (event.detail?.open) {
      this.suspend();
      return;
    }

    this.awaitingRegionRedirectResolution = false;
    this.clearRegionRedirectDeferTimeout();
    this.resumeIfReady();
  }

  bindRegionRedirectListeners() {
    if (!this.shouldDeferForRegionRedirect()) {
      return;
    }

    if (this.isGeolocationResolved() && !this.isRedirectDialogOpen()) {
      return;
    }

    this.awaitingRegionRedirectResolution = true;

    document.addEventListener(
      'regionalise:redirect-statechange',
      this.boundHandleRegionRedirectStateChange
    );

    this.regionRedirectDeferTimeout = window.setTimeout(() => {
      this.awaitingRegionRedirectResolution = false;
      this.resumeIfReady();
    }, CookieBanner.REGION_REDIRECT_DEFER_TIMEOUT_MS);
  }

  resumeIfReady() {
    if (this.hasStoredPreference() || hasStoredConsentResponse()) {
      this.dismiss();
      return;
    }

    if (this.isRegionRedirectBlocking()) {
      return;
    }

    this.open();
  }

  hasStoredPreference() {
    return !!localStorage.getItem('__rmcp2');
  }

  dispatchStateChange(detail = {}) {
    this.dispatchEvent(
      new CustomEvent('cookie-banner:statechange', {
        bubbles: true,
        detail: {
          consentPending: this.consentPending,
          visible: this.visible,
          ...detail,
        },
      })
    );
  }

  updated(changed) {
    if (
      changed.has('visible') ||
      changed.has('consentPending') ||
      changed.has('isOpen')
    ) {
      this.syncHostState();
    }
  }

  syncHostState() {
    this.toggleAttribute('data-consent-pending', this.consentPending);
    this.toggleAttribute('data-visible', this.visible);
    this.style.display = this.visible ? 'block' : 'none';
    this.style.opacity = this.visible && this.isOpen ? '1' : '0';
  }

  open() {
    if (this.isRegionRedirectBlocking()) {
      return;
    }

    suppressDefaultConsentUi();
    this.consentPending = true;
    this.visible = true;
    this.isOpen = false;
    window.__rmuspc = 'YN';
    this.syncHostState();

    requestAnimationFrame(() => {
      this.isOpen = true;
      this.syncHostState();
    });

    this.dispatchStateChange();
  }

  show() {
    this.open();
  }

  connectedCallback() {
    super.connectedCallback();
    suppressDefaultConsentUi();

    if (this.hasStoredPreference() || hasStoredConsentResponse()) {
      this.dismiss();
      return;
    }

    this.consentPending = true;
    this.syncHostState();
    this.dispatchStateChange();
    this.bindRegionRedirectListeners();

    const evaluateVisibility = () => {
      suppressDefaultConsentUi();

      if (this.hasStoredPreference() || hasStoredConsentResponse()) {
        this.dismiss();
      } else if (this.isRegionRedirectBlocking()) {
        this.suspend();
      } else {
        this.open();
      }
    };

    const cookiebotScript = document.getElementById('Cookiebot');

    if (!cookiebotScript) {
      evaluateVisibility();
      return;
    }

    if (window.Cookiebot?.hasResponse !== undefined) {
      evaluateVisibility();
    } else {
      window.addEventListener('CookiebotOnConsentReady', evaluateVisibility, {
        once: true,
      });
    }
  }

  createRenderRoot() {
    return this;
  }

  render() {
    if (!this.visible) {
      return nothing;
    }

    return html`
      <div class="cookie_banner_container">
        <button
          class="cookie_banner_close css_cross"
          @click=${this.hide}
          title="Close"
          aria-label="Close"
        ></button>
        <div class="cookie_banner_title">
          ${this.title}
          ${this.icon_url
            ? html`<img
                src="${this.icon_url}"
                alt="Cookie Icon"
                class="cookie_banner_icon"
              />`
            : ''}
        </div>
        <div class="cookie_banner_divider"></div>
        <div class="cookie_banner_message">
          ${this.message}<button
            type="button"
            class="cookie_banner_preferences"
            @click=${this.openPreferences}
          >
            ${this.preferences_text}
          </button>
        </div>
        <div class="cookie_banner_buttons">
          <a
            id="CybotCookiebotDialogBodyButtonAccept"
            href="javascript:void(0)"
            @click=${this.accept}
            class="cookie_banner_button"
            >${this.accept_text}</a
          >
          <a
            id="CybotCookiebotDialogBodyButtonDecline"
            href="javascript:void(0)"
            @click=${this.decline}
            class="cookie_banner_button cookie_banner_button_decline"
            >${this.decline_text}</a
          >
        </div>
      </div>
    `;
  }

  dismiss() {
    this.clearRegionRedirectDeferTimeout();
    document.removeEventListener(
      'regionalise:redirect-statechange',
      this.boundHandleRegionRedirectStateChange
    );
    this.awaitingRegionRedirectResolution = false;
    this.isOpen = false;
    this.visible = false;
    this.consentPending = false;
    this.syncHostState();
    this.dispatchStateChange();
  }

  hide() {
    this.decline();
  }

  openPreferences() {
    openCookiePreferences();
  }

  accept() {
    persistAcceptedConsent();
  }

  decline() {
    persistDeclinedConsent();
  }
}

customElements.define('cookie-banner', CookieBanner);
